<template>
  <section>
    <section class="py-4" v-if="serviceRequest && order">
      <template v-if="user && Number(user.id) === Number(order.user_id)">
        <div class="row pb-4">
          <div class="col">
            <router-link :to="(isLineLoggedIn) ? '/line-liff/bookings' : '/my-reservations'" class="text-primary-link">{{ $t('message.menu.my_reservations') }}</router-link>
            <i class="fal fa-angle-right"></i> {{ $t('message.my_reservations.service_request_detail') }}
          </div>
        </div>
        <!-- Step view -->
        <div class="pb-4" v-if="serviceRequest.payment_status === 0">
          <progress-bar :serviceRequestStates="serviceRequestStates" :step="serviceRequest.step_data"></progress-bar>
        </div>

        <div class="row pb-3 d-flex h-100">
          <div class="col-8 align-self-center">
            <label class="m-0">
              #{{ order.code }} - <span
              v-html="$options.filters.serviceParentName(order.service_id, globalState.services)"></span>
            </label>
          </div>
          <div class="col-4 text-right">
            <a href="https://line.me/R/ti/p/%40hnk7011n"
               class="btn-outline-info btn"
               target="_blank">{{ $t('message.button.contact_admin') }}</a>
          </div>
        </div>

        <div class="row" v-if="serviceRequest.payment_status === 1">
          <div class="col-12">
            <div class="alert alert-info alert-dismissible fade show" role="alert">
              {{ $t('message.service_request.service_request_remaining_balance_notification') }}
              <button type="button" class="close" data-dismiss="alert" aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-8">
            <!-- Section content -->
            <section>
              <!-- ส่งราคามาให้ลูกค้าแล้ว รอให้ลูกค้ากดยืนยัน หรือ ปฏิเสธ   -->
              <confirm-offer-section :serviceRequest="serviceRequest"
                                     :serviceRequestLostSalesReasons="serviceRequestLostSalesReasons"
                                     @confirmDialogAction="confirmDialogAction"/>

              <!-- Payment section -->
              <payment-section :serviceRequest="serviceRequest" @openPaymentDialog="openPaymentDialog"/>

              <!-- Received Order Section -->
              <received-order-section :order="order"/>

              <!-- Review Section -->
              <review-section :order="order"/>

              <!-- Finnish job photos -->
              <section v-if="order && order.status === 2 && order.order_photos && order.sent_photo_status === 1">
                <div class="card panel-white">
                  <div class="card-body">
                    <div class="mb-2">
                      <label>
                        <i class="fal fa-images"></i> {{ $t('message.my_reservations.completed_cleaning_photos') }}
                      </label>
                    </div>
                    <photos-album :photos="order.order_photos"
                                  :photoType="'order_photos'"
                                  :includeGallery="false"
                                  @onItemClick="setGalleryIndex"/>
                  </div>
                </div>
              </section>

              <!-- Professional contact -->
              <professional-section :order="order" :serviceRequest="serviceRequest" v-if="$mq.phone"/>

              <div class="card panel-white">
                <!-- Status -->
                <div class="card-body">
                  <div class="row no-gutters">
                    <div class="col-md-3 text-left">
                      <label>{{ $t('message.service_request.status') }}</label>
                    </div>
                    <div class="col-md-9">
                      <i class="fas fa-circle fa-sm -status-pending"></i>&nbsp;
                      {{ serviceRequest.service_request_status | multiLangName }}
                    </div>
                  </div>
                </div>

                <!-- Service date -->
                <div class="card-body">
                  <div class="row no-gutters">
                    <div class="col-md-3">
                      <label>{{ $t('message.service_request.service_date') }}</label>
                    </div>
                    <div class="col-md-9">
                      <span v-if="serviceRequest.is_confirmed === 0">
                        {{ serviceRequest.service_date | prettyDate }}
                      </span>
                      <span v-else>
                        <span v-if="serviceRequest.actual_service_date">
                          {{ serviceRequest.actual_service_date | prettyDate }}
                        </span>
                        <span v-else>
                          {{ serviceRequest.offered_service_date | prettyDate }}
                        </span>
                      </span>
                    </div>
                  </div>
                  <div class="row no-gutters mt-3"
                       v-if="serviceRequest.offered_service_date && serviceRequest.is_confirmed === 0">
                    <div class="col-md-3">
                      <label>{{ $t('message.service_request.service_request_provider_offered_service_date') }}</label>
                    </div>
                    <div class="col-md-9 -status-new">
                      {{ serviceRequest.offered_service_date | prettyDate }}
                    </div>
                  </div>
                  <div class="row no-gutters mt-3"
                       v-if="!serviceRequest.offered_service_date && serviceRequest.is_confirmed === 0">
                    <div class="col text-remark">
                      <i class="fas fa-circle fa-sm -status-pending"></i>&nbsp;
                      {{ $t('message.service_request.service_request_service_date_remark') }}
                    </div>
                  </div>
                  <div class="row no-gutters mt-3"
                       v-if="serviceRequest.duration_remark">
                    <div class="col-md-3">
                      <label>{{ $t('message.service_request.service_request_duration_remark') }}</label>
                    </div>
                    <div class="col-md-9">
                      {{ serviceRequest.duration_remark }}
                    </div>
                  </div>
                </div>

                <!-- Purpose -->
                <div class="card-body" v-if="serviceRequest.service_request_purpose">
                  <div class="row no-gutters">
                    <div class="col-md-3">
                      <label>{{ $t('message.service_request.purpose') }}</label>
                    </div>
                    <div class="col-md-9">
                      {{ serviceRequest.service_request_purpose | multiLangName }}
                    </div>
                  </div>
                </div>

                <!-- Building type & size -->
                <div class="card-body" v-if="serviceRequest.building_type != null || serviceRequest.building_size != null">
                  <div class="row no-gutters">
                    <div class="col-md-3">
                      <label v-if="serviceRequest.building_type">
                        {{ $t('message.service_request.building_type') }}
                      </label>
                      <label v-else>
                        {{ $t('message.service_request.furniture_detail') }}
                      </label>
                    </div>
                    <div class="col-md-9">
                      <span v-if="serviceRequest.building_type">
                        {{ serviceRequest.building_type | multiLangName }} - {{ serviceRequest.building_size }}
                      </span>
                      <span v-else>
                        {{ serviceRequest.building_size }}
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Building address -->
                <div class="card-body">
                  <div class="row no-gutters">
                    <div class="col-md-3">
                      <label>{{ $t('message.service_request.address') }}</label>
                    </div>
                    <div class="col-md-9">
                      {{ serviceRequest.building_address }}
                      <span v-if="serviceRequest.sub_district">
                        {{ serviceRequest.sub_district | multiLangName }}
                      </span>
                      {{ serviceRequest.district | multiLangName }}
                      {{ serviceRequest.address_province | multiLangName }}
                    </div>
                  </div>
                </div>

                <!-- Remark -->
                <div class="card-body" v-if="serviceRequest.remark">
                  <div class="row no-gutters">
                    <div class="col-md-3">
                      <label>{{ $t('message.service_request.remark') }}</label>
                    </div>
                    <div class="col-md-9">
                      {{ serviceRequest.remark }}
                    </div>
                  </div>
                </div>

                <!-- Photo -->
                <div class="card-body" v-if="serviceRequest.service_request_photos.length > 0">
                  <label>
                    <i class="fal fa-images"></i> {{ $t('message.service_request.photos') }}
                  </label>
                  <photos-album :photos="serviceRequest.service_request_photos"
                                :photoType="'service_request_photos'"
                                :includeGallery="false"
                                @onItemClick="setGalleryIndex"/>
                </div>

                <!-- Service cost -->
                <div class="card-body" v-if="serviceRequest.service_cost">
                  <div class="row no-gutters">
                    <div class="col-6">
                      <label>{{ $t('message.service_request.service_cost') }}</label>
                    </div>
                    <div class="col-6 text-right">
                      <label>{{ serviceRequest.service_cost | currency }}</label>
                    </div>
                  </div>

                  <div class="row no-gutters">
                    <div class="col-6">
                      {{ $t('message.service_request.service_request_payment_status') }}
                    </div>
                    <div class="col-6 text-right">
                      <span :class="[(serviceRequest.payment_status === 2) ? '-status-new' : 'text-orange']">
                        {{ serviceRequest.payment_status | serviceRequestPaymentStatus }}
                      </span>
                    </div>
                  </div>

                  <!--  Remaining balance  -->
                  <div class="row no-gutters mt-3" v-if="serviceRequest.payment_status === 1">
                    <div class="col-6">
                      <label>{{ $t('message.service_request.service_request_remaining_balance') }}</label>
                    </div>
                    <div class="col-6 text-right" style="font-weight: bold;">
                      <label>{{ parseFloat((serviceRequest.service_cost / 2)).toFixed(2) | currency }}</label>
                    </div>
                    <div class="col-12 text-remark">
                      <i class="fas fa-circle fa-sm -status-pending"></i>
                      {{ $t('message.service_request.service_request_remaining_balance_remark') }}
                    </div>
                  </div>

                  <!--  Service request payments  -->
                  <template v-if="serviceRequest.service_request_payments.length > 0">
                    <hr class="my-4">
                    <div class="row no-gutters my-3">
                      <div class="col-12 text-remark">
                        {{ $t('message.booking.payment_step.payment') }}
                      </div>
                    </div>

                    <div v-for="(payment, index) in serviceRequest.service_request_payments" :key="payment.id">
                      <!--  ประเภท  -->
                      <div class="row no-gutters">
                        <div class="col-6">
                          {{ $t('message.service_request.payment_type') }}
                        </div>
                        <div class="col-6 text-right">
                          {{ payment.payment_type | serviceRequestPaymentType }}
                        </div>
                      </div>
                      <!--  ชำระเมื่อ     -->
                      <div class="row no-gutters mt-2">
                        <div class="col-6">
                          {{ $t('message.service_request.service_request_paid_on') }}
                        </div>
                        <div class="col-6 text-right">
                          {{ payment.created_at | prettyDate }}
                        </div>
                      </div>
                      <!--  สถานะ     -->
                      <div class="row no-gutters mt-2">
                        <div class="col-6">
                          {{ $t('message.service_request.status') }}
                        </div>
                        <div class="col-6 text-right">
                          <span class="-status-new" v-if="payment.status === 1">
                              {{ $t('message.my_reservations.already_paid') }}
                          </span>
                          <span class="text-orange" v-else>
                              {{ $t('message.service_request.order_status_waiting_confirm') }}
                          </span>
                        </div>
                      </div>
                      <!-- total -->
                      <div class="row no-gutters mt-2">
                        <div class="col-6">
                          <label>{{ $t('message.service_request.all_total_cost') }}</label>
                        </div>
                        <div class="col-6 text-right">
                          <label>{{ getTotalCost(payment) | currency }}</label>
                        </div>
                      </div>
                      <!-- Paid by -->
                      <div class="row no-gutters">
                        <div class="col-12">
                          <span v-html="$options.filters.orderPaymentData(payment.payment_data)"></span>
                        </div>
                      </div>
                      <hr v-if="index+1 !== _.size(serviceRequest.service_request_payments)">
                    </div>
                  </template>
                </div>
              </div>


            </section>
          </div>
          <div class="col-md-4">
            <!-- Professional contact -->
            <professional-section :order="order" :serviceRequest="serviceRequest" v-if="!$mq.phone"/>
          </div>
        </div>
        <!-- Back navigation for line liff -->
        <div class="container text-center py-2" v-if="isLineLoggedIn">
          <router-link to="/line-liff/bookings">
            <i class="fas fa-angle-left"></i> {{ $t('message.booking.new_booking.back') }}
          </router-link>
        </div>
        <!-- Gallery -->
        <Tinybox v-model="indexImage" :images="images" loop no-thumbs />
        <payment-modal @fetchData="getServiceRequest"
                       :order="order"
                       :serviceRequest="serviceRequest"
                       ref="paymentModal"/>

      </template>
      <template v-else>
        <div class="p-4" v-if="Number(user.id) !== Number(order.user_id)">
          <div class="row">
            <div class="col text-center">
              <h3>{{ $t('message.my_reservations.cannot_view_other_user_reservation') }}</h3>
            </div>
          </div>
        </div>
      </template>

    </section>
    <section v-else>
      <loader/>
    </section>
  </section>
</template>

<script>
  import { mapGetters, mapState } from 'vuex'
  import axios from 'axios'
  import ProgressBar from './Components/ProgressBar'
  import PaymentModal from './Components/PaymentModal'
  import ConfirmOfferSection from './Components/ConfirmOfferSection'
  import PaymentSection from './Components/PaymentSection'
  import PhotosAlbum from '../order/components/PhotosAlbum'
  import ProfessionalSection from './Components/ProfessionalSection'
  import ReceivedOrderSection from '../order/components/ReceivedOrderSection'
  import ReviewSection from '../order/components/ReviewSection'
  import Loader from '../loader/Loader'

  export default {
    name: 'ServiceRequestDetail',
    components: {
      ConfirmOfferSection,
      PaymentSection,
      PaymentModal,
      ProgressBar,
      PhotosAlbum,
      ProfessionalSection,
      ReceivedOrderSection,
      ReviewSection,
      Loader
    },
    data () {
      return {
        serviceRequest: null,
        serviceRequestStates: null,
        serviceRequestLostSalesReasons: null,
        professionalQuotation: null,
        order: null,
        images: null,
        indexImage: null
      }
    },
    computed: {
      ...mapGetters({
        globalState: 'globalVal/getState',
        payment: 'payment/getPayment',
        user: 'auth/getUser'
      }),
      ...mapState('auth', ['isLineLoggedIn']),
      ...mapState({
        isAddingReportIssue: state => state.reportIssue.isAdding,
        isAddingReceivedOrder: state => state.receivedOrder.isAdding,
        isAddingReview: state => state.review.isAdding
      })
    },
    watch: {
      isAddingReportIssue () {
        console.log('isAddingReportIssue', this.isAddingReportIssue)
        if (!this.isAddingReportIssue) {
          this.getServiceRequest()
        }
      },
      isAddingReceivedOrder () {
        console.log('isAddingReceivedOrder', this.isAddingReceivedOrder)
        if (!this.isAddingReceivedOrder) {
          this.getServiceRequest()
        }
      },
      isAddingReview () {
        console.log('isAddingReview', this.isAddingReview)
        if (!this.isAddingReview) {
          this.getServiceRequest()
        }
      }
    },
    methods: {
      setGalleryIndex (index, type) {
        if (type === 'service_request_photos') {
          this.images = this._.map(this.serviceRequest.service_request_photos, 'image_xl')
        } else {
          this.images = this._.map(this.order.order_photos, 'image_xl')
        }
        this.indexImage = index
      },
      confirmDialogAction (result) {
        if (result) {
          this.getServiceRequest()
        }
      },
      getTotalCost (payment = null) {
        let calculate = Number(payment.total_price) + Number(payment.service_vat)
        return calculate.toFixed(2)
      },
      openPaymentDialog () {
        this.$refs.paymentModal.openModal(this.serviceRequest.id)
      },
      async getServiceRequest () {
        let {data} = await axios.get('/service-requests/' + this.$route.params.id)
        if (!data.error) {
          this.serviceRequest = data.service_request
          this.serviceRequestStates = data.service_request_states
          this.serviceRequestLostSalesReasons = data.service_request_lost_sales_reasons
          this.order = data.order
          this.professionalQuotation = data.professional_quotation
        } else {
          this.$swal({text: 'ผิดพลาด ' + data.message, type: 'error'})
        }
      }
    },
    mounted () {
      this.getServiceRequest()
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/serviceRequest/ServiceRequestDetail.vue